import { useEffect, useRef, useState } from "react";
import { Video, Square, Circle, Upload, Loader2, RotateCcw, ChevronRight, Check, ListChecks } from "lucide-react";
import { toast } from "sonner";

/**
 * Guided video capture with chapter prompts.
 * Props:
 *  - chapters: [{ id, label, hint, urgency? }]
 *  - onUpload: async (file, chaptersWithTs) => void
 *  - busy: bool
 */
export default function GuidedVideoCapture({ chapters = [], onUpload, busy }) {
  const [mode, setMode] = useState("idle"); // idle | recording | preview
  const [stream, setStream] = useState(null);
  const [blob, setBlob] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [chapterTimestamps, setChapterTimestamps] = useState([]); // [{...chapter, ts: seconds}]
  const videoRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);

  useEffect(() => () => stopAll(), []); // eslint-disable-line

  const stopAll = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      try { mediaRef.current.stop(); } catch {}
    }
    if (stream) stream.getTracks().forEach(t => t.stop());
  };

  const startRecording = async () => {
    if (chapters.length === 0) { toast.error("No inspection steps configured"); return; }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 } },
        audio: true,
      });
      setStream(s);
      // Flip to `recording` FIRST so React mounts the <video> element in the
      // DOM — otherwise `videoRef.current` is null when we try to attach
      // srcObject and the tech sees a black rectangle instead of the live feed.
      setMode("recording");
      setSeconds(0);
      await new Promise((r) => requestAnimationFrame(() => r()));
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        try { await videoRef.current.play(); } catch (_) { /* autoplay policies */ }
      }
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus" : "video/webm";
      const mr = new MediaRecorder(s, { mimeType: mime });
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "video/webm" });
        setBlob(b);
        setMode("preview");
        s.getTracks().forEach(t => t.stop());
      };
      mr.start(1000);
      startedAtRef.current = Date.now();
      // mark first chapter at t=0
      setChapterTimestamps([{ ...chapters[0], ts: 0 }]);
      setStepIndex(0);
      timerRef.current = setInterval(() => setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000)), 250);
    } catch (e) {
      console.error(e);
      toast.error("Could not access camera. Use file upload instead.");
    }
  };

  const nextStep = () => {
    const next = stepIndex + 1;
    if (next >= chapters.length) {
      stopRecording();
      return;
    }
    const ts = Math.floor((Date.now() - startedAtRef.current) / 1000);
    setChapterTimestamps((prev) => [...prev, { ...chapters[next], ts }]);
    setStepIndex(next);
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
  };

  const reset = () => {
    setBlob(null); setSeconds(0); setMode("idle"); setStepIndex(0); setChapterTimestamps([]);
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBlob(f); setMode("preview"); setChapterTimestamps([]); // free upload has no chapters
  };

  const upload = async () => {
    if (!blob) return;
    const file = blob instanceof File ? blob : new File([blob], `recording-${Date.now()}.webm`, { type: "video/webm" });
    await onUpload(file, chapterTimestamps);
    reset();
  };

  const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
  const previewUrl = blob ? URL.createObjectURL(blob) : null;
  const current = chapters[stepIndex];

  return (
    <div className="card-default p-5" data-testid="guided-video-capture">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="label-overline">Guided VHC walkaround</div>
          <div className="font-display font-bold text-xl mt-1">Record the inspection</div>
        </div>
        {mode === "idle" && (
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <ListChecks className="w-3.5 h-3.5" /> {chapters.length} steps
          </div>
        )}
      </div>

      <div className="aspect-video bg-foreground rounded-lg overflow-hidden relative">
        {mode === "recording" && <video ref={videoRef} className="w-full h-full object-cover" playsInline />}
        {mode === "preview" && previewUrl && (
          <video src={previewUrl} controls className="w-full h-full object-cover" data-testid="video-preview" />
        )}
        {mode === "idle" && (
          <div className="absolute inset-0 grid place-items-center text-background/70">
            <div className="text-center">
              <Video className="w-10 h-10 mx-auto opacity-60" />
              <div className="text-sm mt-2 opacity-70">Press Start to begin the guided walkaround</div>
            </div>
          </div>
        )}
        {mode === "recording" && (
          <>
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-destructive text-destructive-foreground px-3 py-1 rounded-full text-xs font-bold">
              <Circle className="w-2 h-2 fill-current animate-pulse" /> REC {fmt(seconds)}
            </div>
            <div className="absolute top-3 right-3 bg-black/70 text-white text-xs font-bold px-2 py-1 rounded">
              Step {stepIndex + 1} / {chapters.length}
            </div>
            {/* Prompt overlay */}
            <div className="absolute left-3 right-3 bottom-3 bg-black/80 backdrop-blur text-white rounded-lg p-3 border border-white/10">
              <div className="text-[10px] font-bold uppercase tracking-widest text-primary">Now film</div>
              <div className="font-display font-bold text-lg mt-0.5">{current?.label}</div>
              {current?.hint && <div className="text-xs text-white/80 mt-1">{current.hint}</div>}
            </div>
          </>
        )}
      </div>

      {/* Step progress bar */}
      {mode === "recording" && (
        <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden" data-testid="step-progress">
          <div className="h-full bg-primary transition-all" style={{ width: `${((stepIndex + 1) / chapters.length) * 100}%` }} />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        {mode === "idle" && (
          <>
            <button onClick={startRecording} className="btn-primary" data-testid="record-start-btn">
              <Circle className="w-4 h-4 fill-current" /> Start guided recording
            </button>
            <label className="btn-outline cursor-pointer" data-testid="upload-file-label">
              <Upload className="w-4 h-4" /> Upload existing video
              <input type="file" accept="video/*" className="hidden" onChange={onFile} data-testid="upload-file-input" />
            </label>
          </>
        )}
        {mode === "recording" && (
          <>
            <button onClick={nextStep} className="btn-primary flex-1" data-testid="next-step-btn">
              {stepIndex + 1 >= chapters.length ? <Check className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              {stepIndex + 1 >= chapters.length ? "Finish & stop" : `Next step → ${chapters[stepIndex + 1]?.label}`}
            </button>
            <button onClick={stopRecording} className="btn-outline" data-testid="record-stop-btn">
              <Square className="w-4 h-4 fill-current" /> Stop now
            </button>
          </>
        )}
        {mode === "preview" && (
          <>
            <button onClick={upload} disabled={busy} className="btn-primary" data-testid="video-upload-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Save video {chapterTimestamps.length > 0 ? `(${chapterTimestamps.length} chapters)` : ""}
            </button>
            <button onClick={reset} className="btn-outline" data-testid="video-reset-btn">
              <RotateCcw className="w-4 h-4" /> Re-do
            </button>
          </>
        )}
      </div>

      {/* Chapter chips when previewing */}
      {mode === "preview" && chapterTimestamps.length > 0 && (
        <div className="mt-4">
          <div className="label-overline mb-2">Chapters captured</div>
          <div className="flex flex-wrap gap-1.5">
            {chapterTimestamps.map((c, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded bg-muted border border-border font-mono-plex">
                {fmt(c.ts)} · {c.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Step list when idle */}
      {mode === "idle" && chapters.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground">
            See the {chapters.length} steps
          </summary>
          <ol className="mt-2 text-sm space-y-1">
            {chapters.map((c, i) => (
              <li key={c.id} className="flex gap-2">
                <span className="font-mono-plex text-muted-foreground w-6">{String(i+1).padStart(2,"0")}</span>
                <span className="font-medium">{c.label}</span>
                {c.urgency && (
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    c.urgency === "red" ? "bg-destructive/15 text-destructive" : "bg-warning/15 text-warning"
                  }`}>{c.urgency}</span>
                )}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}
